// src/public-routes/sectors/EcommerceSector.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Progress, Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
    FaShoppingBag,
    FaSearch,
    FaBullseye,
    FaSync,
    FaUsers,
    FaCogs,
    FaShieldAlt,
    FaChartLine,
    FaRocket,
    FaTrophy
} from 'react-icons/fa';

const EcommerceSector = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [loadingPercent, setLoadingPercent] = useState(0);
    const loadingRef = useRef(null);

    // Handle percentage-based loading
    useEffect(() => {
        window.scrollTo(0, 0);
        if (!isLoading) return;

        const interval = setInterval(() => {
            setLoadingPercent(prev => {
                const newPercent = Math.min(prev + 5, 100);

                if (newPercent === 100) {
                    clearInterval(interval);
                    setTimeout(() => {
                        setIsLoading(false);
                        window.scrollTo(0, 0);
                    }, 500);
                }

                return newPercent;
            });
        }, 100);

        return () => clearInterval(interval);
    }, [isLoading]);

    const stats = [
        { number: '4.2T', label: 'Global Ecommerce Sales by 2025' },
        { number: '2.14B', label: 'Digital Buyers Worldwide' },
        { number: '3.5x', label: 'Avg ROI on Ecommerce SEO' },
        { number: '76%', label: 'Growth in Mobile Commerce' }
    ];

    const approaches = [
        {
            title: "SEO & Organic Growth",
            description: "Dominate search results with technical SEO, product page optimization, and content strategies that drive qualified organic traffic.",
            icon: FaSearch
        },
        {
            title: "Performance Marketing",
            description: "Maximize ROAS with data-driven PPC, shopping ads, social commerce, and retargeting campaigns optimized for conversion.",
            icon: FaBullseye
        },
        {
            title: "CRO & UX Optimization",
            description: "Turn visitors into customers with A/B testing, checkout optimization, and personalized user experiences that reduce cart abandonment.",
            icon: FaSync
        },
        {
            title: "Customer Retention",
            description: "Build lasting relationships through email automation, loyalty programs, and personalized marketing that drives repeat purchases.",
            icon: FaUsers
        }
    ];

    const benefits = [
        ['Increased Conversion Rates', 'Turn more visitors into paying customers with optimized product pages and checkout flows'],
        ['Lower Customer Acquisition Cost', 'Reduce dependency on paid ads through organic search and retention strategies'],
        ['Higher Average Order Value', 'Increase revenue per customer with strategic upselling and cross-selling'],
        ['Sustainable Growth', 'Build a scalable ecommerce engine that compounds over time'],
        ['Competitive Advantage', 'Stand out in crowded markets with unique, data-driven strategies']
    ];

    const questions = [
        'How can you reduce cart abandonment and increase conversion rates?',
        'What strategies will drive profitable, scalable growth for your store?',
        'How do you compete with marketplace giants like Amazon?',
        'Which channels deliver the highest ROI for ecommerce?',
        'How can you build customer loyalty in a price-sensitive market?'
    ];

    return (
        <>



            <div className="st-page">
                {/* Top Image Section */}
                <div className="st-decorative-image-container">
                    <img
                        src={require('./Ecommerce.PNG')}
                        alt="Ecommerce Digital Marketing - Scale Your Online Store"
                        loading="eager"
                    />
                </div>

                {/* Title Section */}
                <div className="sv-page-title-section">
                    <div className="container">
                        <h1>
                            <span className="sv-gradient-text">Ecommerce</span> Digital Marketing
                        </h1>
                        <p>Scale profitably through data-driven campaigns, conversion-focused strategies, and continuous optimisation</p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container-fluid sv-content-wrapper">
                    <div className="sv-main-content">
                        {/* Hero Section */}
                        <section className="sv-hero-section" data-animated>
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <p className="sv-lead">
                                        In the highly competitive <span className="sv-keyword-highlight">ecommerce space</span>, we help brands scale profitably through data-driven campaigns, conversion-focused strategies, and continuous optimisation.
                                    </p>
                                    <p className="sv-description">
                                        At Reachtix, our <strong>ecommerce digital marketing services</strong> combine strategic thinking, data intelligence, and advanced tools to create campaigns that not only perform — but differentiate your brand. From acquisition to retention, we build systems that drive sustainable growth.
                                    </p>
                                    <p className="sv-description">
                                        We understand the unique challenges ecommerce brands face: rising customer acquisition costs, increasing competition, and the need to balance short-term sales with long-term brand building. Our sector-specific insight helps you navigate these challenges effectively.
                                    </p>
                                    <div className="sv-cta-buttons">
                                        <Button tag={Link} to="/contact" color="dark">
                                            <i className="fas fa-rocket mr-2"></i>
                                            Start Ecommerce Strategy
                                        </Button>
                                        <Button tag={Link} to="/contact" color="dark">
                                            <i className="fas fa-comments mr-2"></i>
                                            Get Free Consultation
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Stats Section */}
                        <section className="sv-stats-section">
                            <h2 className="sv-section-title" data-animated>
                                Why <span className="sv-text-gradient">Ecommerce Marketing</span> Matters
                            </h2>
                            <Row className="sv-stats-grid">
                                {stats.map((stat, index) => (
                                    <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                        <div className="sv-stat-card">
                                            <div className="sv-stat-number">{stat.number}</div>
                                            <div className="sv-stat-label">{stat.label}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </section>

                        {/* What We Do Section */}
                        <section className="sv-explanation-section" data-animated>
                            <Row>
                                <Col lg={8}>
                                    <h2 className="sv-section-title">What We Do for Ecommerce Brands</h2>
                                    <p className="sv-section-description">
                                        <strong>Ecommerce digital marketing</strong> goes beyond just driving traffic. It's about creating a seamless customer journey that converts visitors into loyal customers while maximizing lifetime value and reducing acquisition costs.
                                    </p>
                                    <div className="sv-questions-grid">
                                        <h4>Key Questions We Answer:</h4>
                                        {questions.map((question, index) => (
                                            <div key={index} className="sv-question-item">
                                                <i className="fas fa-question-circle"></i>
                                                {question}
                                            </div>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Benefits Overview */}
                        <section className="sv-benefits-overview" data-animated>
                            <Row>
                                <Col xl={12}>
                                    <h2 className="sv-section-title">Why Invest in Ecommerce Marketing?</h2>
                                    <p className="sv-section-description">
                                        The ecommerce landscape is more competitive than ever. A comprehensive digital marketing strategy is essential to stand out, attract qualified customers, and build a sustainable online business that grows over time.
                                    </p>
                                    <div className="sv-benefits-cards">
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-chart-line"></i>
                                            <h5>Sustainable Growth</h5>
                                            <p>Build a long-term asset that continues to drive traffic and sales over time</p>
                                        </div>
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-dollar-sign"></i>
                                            <h5>Cost-Effective</h5>
                                            <p>Higher ROI than traditional advertising with compounding returns</p>
                                        </div>
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-users"></i>
                                            <h5>Qualified Traffic</h5>
                                            <p>Attract customers actively searching for your products</p>
                                        </div>
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-trophy"></i>
                                            <h5>Competitive Edge</h5>
                                            <p>Outperform competitors and capture market share in your niche</p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Our Approach Section */}
                        <section className="sv-approach-section" data-animated>
                            <Row>
                                <Col>
                                    <h2 className="sv-section-title">Our Ecommerce Marketing Services</h2>
                                    <p className="sv-section-description">
                                        Reachtix offers a comprehensive suite of ecommerce marketing services designed to address every aspect of your online store's growth — from customer acquisition to retention and optimization.
                                    </p>

                                    <Row className="sv-services-grid">
                                        {approaches.map((service, index) => (
                                            <Col lg={6} md={6} key={index} className="mb-4">
                                                <div className="sv-approach-card p-4 h-100">
                                                    <div className="sv-approach-icon">
                                                        <service.icon />
                                                    </div>
                                                    <div className="sv-approach-content">
                                                        <h4>{service.title}</h4>
                                                        <p className="service-description">{service.description}</p>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Col>
                            </Row>
                        </section>

                        {/* Benefits Table Section */}
                        <section className="sv-benefits-section" data-animated>
                            <Row>
                                <Col>
                                    <h2 className="mt-5 sv-section-title">Key Benefits of Ecommerce Marketing With Reachtix</h2>
                                    <div className="table-responsive">
                                        <table className="table sv-benefits-table">
                                            <thead>
                                                <tr>
                                                    <th>Core Advantage</th>
                                                    <th>Business Impact</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {benefits.map(([advantage, impact], index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <i className="fas fa-check-circle"></i>
                                                            <strong>{advantage}</strong>
                                                        </td>
                                                        <td>{impact}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Main CTA Section */}
                        <section className="sv-main-cta" data-animated>
                            <Row>
                                <Col>
                                    <h2>Ready to Scale Your Ecommerce Business?</h2>
                                    <p className="sv-cta-description">
                                        Whether you're launching a new store or looking to optimize an existing one, our comprehensive ecommerce marketing services provide the expertise and results you need to dominate your market.
                                    </p>
                                    <p className="sv-cta-description">
                                        Let's build a digital strategy that drives profitable growth and delivers measurable ROI for your online store.
                                    </p>
                                    <div className="sv-cta-buttons">
                                        <Button tag={Link} to="/contact" color="primary">
                                            <i className="fas fa-calendar-check mr-2"></i>
                                            Start Ecommerce Consultation
                                        </Button>
                                        <Button tag={Link} to="/contact" color="light">
                                            <i className="fas fa-clipboard-list mr-2"></i>
                                            Get Store Audit
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EcommerceSector;